import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { elementAction } from "../../store";
import { useSelector } from "react-redux";
import { Keyboard } from "../control";
import Corner from "./corner";
export default function Select({
  t,
  className = "",
  enabled = true,
  name,
  placeHolder,
  options,
  value,
  changeValue,
  options_i18n = "category_options",
  isFocus = () => false,
}) {
  const dispatch = useDispatch();
  const theme = useSelector((state) => state.element.theme);
  const selectRef = useRef(null);
  const targetRef = useRef(null);
  const optionRef = useRef(null);

  const [showOptions, setShowOptions] = useState(false);
  const [selected, setSelected] = useState(0);
  const [hasFocus, setHasFocus] = useState(false);
  const categoryI18n = t(options_i18n, { returnObjects: true });

  useEffect(() => {
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, []);

  useEffect(() => {
    setSelected(0);
  }, [options]);

  useEffect(() => {
    dispatch(elementAction.setIsShowSelect(showOptions));
  }, [showOptions]);

  useEffect(() => {
    checkScroll();
  }, [selected]);

  const selectedItemStyle = () => {
    return hasFocus
      ? ` border-${theme}-on-focus border-thin placeholder-${theme}-focus`
      : ` border-${theme}-normal ${
          enabled
            ? ` border-thin placeholder-${theme}-normal `
            : ` placeholder-${theme}-normal `
        }`;
  };

  const label = () => categoryI18n[value] || "";
  const getItem = (data) => (data.value ? data.value : data);

  const _changeValue = (val) => {
    console.log("✅ _changeValue", val);
    changeValue(val);
    setShowOptions(false);

    requestAnimationFrame(() => {
      console.log("✅ requestAnimationFrame trigger");
      setHasFocus(false);
      dispatch(elementAction.setIsShowSelect(false));
      keyEsc();

      // 確保選到的項目在可見範圍內
      const el = document.getElementById(val);
      if (el) {
        el.scrollIntoView({
          block: "nearest",
          inline: "nearest",
          behavior: "smooth",
        });
      }
    });
  };

  const handleClickOutside = (event) => {
    if (
      targetRef.current &&
      !targetRef.current.contains(event.target) &&
      optionRef.current &&
      !optionRef.current.contains(event.target)
    ) {
      setShowOptions(false);
      setHasFocus(false);
      dispatch(elementAction.setIsShowSelect(false));
    }
  };

  const triggerOptions = () => {
    setShowOptions((prev) => !prev);
    setHasFocus(true);
  };

  const checkScroll = () => {
    try {
      const selectedEl = document.getElementById(getItem(options[selected]));
      if (selectedEl) {
        selectedEl.scrollIntoView({
          block: "nearest",
          inline: "nearest",
          behavior: "smooth",
        });
      }
    } catch (error) {}
  };

  const keyEnter = () => {
    _changeValue(getItem(options[selected]));
    dispatch(elementAction.setIsShowSelect(true));
  };

  const keyEsc = () => {
    setShowOptions(false);
    setHasFocus(false);
    dispatch(elementAction.setIsShowSelect(false));
  };

  const selectCss = (item, index) => {
    const isSelected = selected === index;
    return isSelected
      ? ` bg-${theme}-selected `
      : value === getItem(item)
      ? `bg-${theme}-option-selected hover:bg-${theme}-selected hover:text-${theme}-normal`
      : ` hover:bg-${theme}-selected bg-transparent `;
  };

  return (
    <div
      className={`${className} group hover:border-${theme}-btn-hover hover:bg-${theme}-btn-hover hover:bg-opacity-30 corner-outline rounded-md border-thin border-${theme}-normal flex flex-col h-[40px] xl:w-1/3 md:w-2/3 bg-transparent`} // 調整背景色為透明
      data-id={name}
      id={name}
      tabIndex={enabled ? 0 : -1}
      onFocus={() => setHasFocus(true)}
      onBlur={() => setHasFocus(false)}
      onClick={triggerOptions}
    >
      <Corner />
      <input
        disabled
        aria-label={placeHolder}
        aria-roledescription={"select"}
        className={
          ` h-full w-full px-4 input-text text-${theme}-normal placeholder-${theme}-normal bg-transparent` +
          (showOptions ? `border-thin border-${theme}-normal ` : " ")
        }
        placeholder={placeHolder}
        autoComplete="off"
        value={label()}
      />
      <div
        ref={targetRef}
        role="button"
        aria-hidden={"true"}
        className={"h-full w-full bg-transparent absolute px-4"} // 調整背景色為透明
      />
      <div
        className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700"
        aria-hidden={"true"}
      >
        <svg
          width="28"
          height="28"
          viewBox="0 0 28 28"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M13.9954 18.667L19.834 9.33366L8.16732 9.33366L13.9954 18.667Z"
            className={`fill-current ${
              theme === "light" ? "text-black" : "text-white"
            } group-hover:text-${theme}-btn-hover`}
          />
        </svg>
      </div>
      {showOptions && (
        <div className="inset-0 bg-transparent" ref={optionRef}>
          <Keyboard
            isEnabled={showOptions}
            options={options.map((o) => [getItem(o)])}
            selected={[selected, 0]}
            setSelected={([row]) => setSelected(row)}
            keyEnter={keyEnter}
            keyEsc={keyEsc}
          />
          <div
            ref={selectRef}
            className={`rounded-md absolute left-0 top-0 mt-[40px] z-10 font-light text-[22px]
                        text-${theme}-normal bg-${theme}-option min-w-full max-h-[45vh] scrollbar overflow-y-auto`}
          >
            {options.map((item, index) => (
              <div
                id={getItem(item)}
                key={index}
                className={`rounded-md w-full h-8 px-3 py-2 input-text ${selectCss(
                  item,
                  index
                )}`}
                tabIndex={0}
                aria-label={categoryI18n[getItem(item)]}
                aria-roledescription={"option"}
                onMouseDown={() => {
                  setSelected(index);
                  _changeValue(getItem(item));
                }}
                onClick={() => {
                  setSelected(index);
                  _changeValue(getItem(item));
                }}
              >
                <p>{categoryI18n[getItem(item)]}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}